/**
 * Validation boundary and display formatter for a configured Pay by Bank loyalty points
 * multiplier (the `payByBankPointsMultiplier` GraphQL Float).
 *
 * A multiplier only represents a boost when it exceeds 1x: "1x", "0.5x" and values that
 * round down to 1x are not an offer, so they resolve to null and callers fail closed.
 * An upper bound is intentionally not enforced here pending Product confirmation; only
 * non-finite values are rejected above the 1x floor.
 */

const roundToTwoDecimals = (value: number): number => Math.round(value * 100) / 100;

/**
 * Returns the configured multiplier rounded to 2 decimals (float artifacts such as
 * 2.1000000000000001), or null when the value is missing, non-finite, or not greater
 * than 1x after rounding.
 */
export function resolvePointsBoostMultiplier(multiplier: number | null | undefined): number | null {
  if (multiplier === null || multiplier === undefined || !Number.isFinite(multiplier)) {
    return null;
  }

  const rounded = roundToTwoDecimals(multiplier);

  return rounded > 1 ? rounded : null;
}

/**
 * Formats a configured multiplier for display, e.g. "2x" for whole numbers and "2.5x" for
 * decimals, trimming trailing zeros so a configured 2.50 renders as "2.5x".
 *
 * Returns null for a missing/invalid/non-boost multiplier so callers can treat that as
 * "don't render the boost messaging" without duplicating the validity check.
 */
export function formatPointsMultiplier(multiplier: number | null | undefined): string | null {
  const resolved = resolvePointsBoostMultiplier(multiplier);

  if (resolved === null) {
    return null;
  }

  const formatted = Number.isInteger(resolved)
    ? String(resolved)
    : String(resolved).replace(/0+$/, '').replace(/\.$/, '');

  return `${formatted}x`;
}
